/**
 * Forms domain event schemas.
 *
 * Typed payload validation for all form-engine Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module forms.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const FormPublishedEvent = BackboneEventSchema.extend({
  domain: z.literal('forms'),
  type: z.literal('form_published'),
  payload: z.object({
    form_id: z.string(),
    version_id: z.string(),
    slug: z.string().optional(),
    source: z.string(),
  }),
})

export const FormSubmissionReceivedPayloadSchema = z.object({
  submission_id: z.string(),
  form_id: z.string(),
  version_id: z.string(),
  organization_id: z.string().optional(),
  submitter_user_id: z.string().nullable().optional(),
  status: z.enum(['partial', 'complete', 'spam']).optional(),
  duration_ms: z.number().optional(),
  source: z.string(),
})

export const FormSubmissionReceivedEvent = BackboneEventSchema.extend({
  domain: z.literal('forms'),
  type: z.literal('form_submission_received'),
  payload: FormSubmissionReceivedPayloadSchema,
})

export const FormSubmissionCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('forms'),
  type: z.literal('form_submission_completed'),
  payload: z.object({
    submission_id: z.string(),
    form_id: z.string(),
    version_id: z.string(),
    actions_run: z.array(z.string()).optional(),
    source: z.string(),
  }),
})

export const FormTemplateInstalledEvent = BackboneEventSchema.extend({
  domain: z.literal('forms'),
  type: z.literal('form_template_installed'),
  payload: z.object({
    template_id: z.string(),
    template_slug: z.string().optional(),
    form_id: z.string(),
    organization_id: z.string().optional(),
    source: z.string(),
  }),
})

// ── Registry array (used by validation.ts) ──────────────────────────────

export const FormsDomainEvents = [
  FormPublishedEvent,
  FormSubmissionReceivedEvent,
  FormSubmissionCompletedEvent,
  FormTemplateInstalledEvent,
] as const

// ── Discriminated union ─────────────────────────────────────────────────

export const FormsDomainEvent = z.discriminatedUnion('type', [
  FormPublishedEvent,
  FormSubmissionReceivedEvent,
  FormSubmissionCompletedEvent,
  FormTemplateInstalledEvent,
])

export type FormsDomainEventType = z.infer<typeof FormsDomainEvent>

export type FormSubmissionReceivedPayload = z.infer<typeof FormSubmissionReceivedEvent>['payload']
